import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    LayoutDashboard,
    Users,
    MessageCircle,
    Calendar,
    BookOpen,
    Building2,
    User,
    LogOut,
    Menu,
    X,
    Settings,
    Search,
    Plus,
    Sun,
    Moon,
} from 'lucide-react';
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { MyEnAbLogo } from './MyEnAbLogo';
const PostModal = lazy(() => import('./PostModal'));
import axios from 'axios';
import { API_URL as API } from '../config';

const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/forums', label: 'Forums', icon: MessageCircle },
    { path: '/directory', label: 'Directory', icon: Building2 },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/resources', label: 'Resources', icon: BookOpen },
    { path: '/network', label: 'My Network', icon: Users },
    { path: '/community', label: 'Find People', icon: Search },
];

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [visitCount, setVisitCount] = useState(null);

    const fetchPendingCount = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/connections/pending`);
            setPendingCount(response.data.length);
        } catch (error) {
            console.error('Failed to fetch pending count:', error);
        }
    }, []);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get(`${API}/stats`);
                setVisitCount(response.data.visits);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            }
        };

        fetchStats();
        if (user) {
            fetchPendingCount();
            const interval = setInterval(fetchPendingCount, 120000);
            return () => clearInterval(interval);
        }
    }, [user, fetchPendingCount]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)', color: 'var(--text-primary)' }}>
            {/* WCAG 2.4.1 - Skip to main content link */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded-lg focus:font-semibold focus:shadow-lg"
            >
                Skip to main content
            </a>

            {/* WCAG 4.1.3 - Live region */}
            <div aria-live="polite" aria-atomic="true" className="sr-only" id="a11y-status-live" />

            {/* Top Navigation */}
            <nav
                className="sticky top-0 z-50 h-16 flex items-center backdrop-blur-sm"
                style={{ borderBottom: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-nav)' }}
                role="navigation"
                aria-label="Main navigation"
            >
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    {/* Logo */}
                    <Link
                        to={user ? '/dashboard' : '/'}
                        className="flex items-center focus:outline-none focus:ring-0"
                        data-testid="nav-logo"
                        aria-label="MyEnAb — Go to homepage"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    >
                        <MyEnAbLogo className="w-48 h-14" />
                    </Link>

                    {/* Right Side */}
                    <div className="flex items-center gap-2">
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-full transition-all border"
                            style={{ color: 'var(--text-muted)', borderColor: 'var(--border-subtle)', backgroundColor: 'transparent' }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--hover-overlay)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>

                        {user ? (
                            <>
                                <button
                                    onClick={() => {
                                        const params = new URLSearchParams(location.search);
                                        params.set('action', 'new_post');
                                        navigate({ search: params.toString() });
                                    }}
                                    className="p-2 rounded-full transition-all border"
                                    style={{ color: 'var(--text-muted)', borderColor: 'var(--border-subtle)', backgroundColor: 'transparent' }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--hover-overlay)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                                    title="Create Post"
                                    data-testid="nav-create-post"
                                >
                                    <Plus className="w-5 h-5" />
                                </button>
                                <Link
                                    to="/messages"
                                    className="relative p-2 rounded-full transition-all border"
                                    style={{ color: 'var(--text-muted)', borderColor: 'var(--border-subtle)' }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--hover-overlay)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                                    data-testid="nav-messages"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                </Link>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-full" data-testid="user-menu-trigger">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-purple-600 flex items-center justify-center">
                                                <span className="text-sm font-bold text-white">{(user.name || 'U').charAt(0).toUpperCase()}</span>
                                            </div>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-56"
                                        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
                                    >
                                        <div className="px-3 py-2">
                                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
                                        </div>
                                        <DropdownMenuSeparator style={{ backgroundColor: 'var(--border-subtle)' }} />
                                        <DropdownMenuItem asChild>
                                            <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="menu-profile">
                                                <User className="w-4 h-4" />
                                                Profile
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/settings" className="flex items-center gap-2 cursor-pointer" data-testid="menu-settings">
                                                <Settings className="w-4 h-4" />
                                                Settings
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator style={{ backgroundColor: 'var(--border-subtle)' }} />
                                        <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-400 cursor-pointer" data-testid="menu-logout">
                                            <LogOut className="w-4 h-4 mr-2" />
                                            Logout
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Mobile Menu Button */}
                                <button
                                    className="md:hidden p-2 rounded-lg transition-colors"
                                    style={{ color: 'var(--text-muted)', backgroundColor: 'transparent' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--hover-overlay)'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label="Toggle navigation menu"
                                    aria-expanded={mobileMenuOpen}
                                    aria-controls="mobile-menu"
                                    data-testid="mobile-menu-toggle"
                                >
                                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Link to="/login">
                                    <Button variant="ghost" className="btn-secondary" data-testid="nav-login">
                                        Log in
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button className="btn-primary text-sm px-6 py-2" data-testid="nav-register">
                                        Join Now
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            {user && mobileMenuOpen && (
                <div
                    id="mobile-menu"
                    className="md:hidden"
                    style={{ borderBottom: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-nav)' }}
                >
                    <div className="px-4 py-4 space-y-2">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                                    style={{
                                        backgroundColor: isActive ? 'var(--active-overlay)' : 'transparent',
                                        color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                                        fontWeight: isActive ? '500' : '400',
                                    }}
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="font-medium flex-1">{item.label}</span>
                                    {item.path === '/network' && pendingCount > 0 && (
                                        <span className="bg-inclusion-red text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                                            {pendingCount}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Layout Wrapper */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-8 w-full min-h-[calc(100vh-4rem)] pt-6">

                {/* Left Sidebar (Desktop Only) */}
                {user && (
                    <aside className="hidden md:block w-64 flex-shrink-0" aria-label="Sidebar Navigation">
                        <div className="sticky top-24 space-y-6">
                            {/* Profile Snippet */}
                            <div
                                className="rounded-xl p-4 overflow-hidden relative border"
                                style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}
                            >
                                <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-r from-inclusion-blue/20 to-inclusion-purple/20"></div>
                                <div className="relative pt-4 flex flex-col items-center">
                                    <div
                                        className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-purple-600 flex items-center justify-center border-4 mb-2 shadow-sm"
                                        style={{ borderColor: 'var(--bg-card)' }}
                                    >
                                        <span className="text-xl font-bold text-white">{(user.name || 'U').charAt(0).toUpperCase()}</span>
                                    </div>
                                    <h3 className="font-lexend font-semibold text-center line-clamp-1" style={{ color: 'var(--text-primary)' }}>{user.name}</h3>
                                    <p className="text-xs text-center uppercase tracking-wider font-semibold mt-1" style={{ color: 'var(--text-muted)' }}>{user.user_type?.replace('_', ' ')}</p>
                                </div>
                            </div>

                            {/* Vertical Navigation Links */}
                            <nav className="flex flex-col gap-1">
                                {navItems.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                                            style={{
                                                backgroundColor: isActive ? 'var(--active-overlay)' : 'transparent',
                                                color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                                                fontWeight: isActive ? '500' : '400',
                                            }}
                                            data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
                                        >
                                            <Icon className="w-5 h-5" style={{ color: isActive ? 'var(--text-primary)' : 'var(--text-muted)' }} />
                                            <span className="flex-1">{item.label}</span>
                                            {item.path === '/network' && pendingCount > 0 && (
                                                <span className="bg-inclusion-red text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center animate-pulse">
                                                    {pendingCount}
                                                </span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>
                )}

                {/* Main Content Area */}
                <main id="main-content" className="flex-1 min-w-0 pb-12">
                    {children}
                </main>
            </div>

            {/* Footer */}
            <footer
                className="border-t"
                style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--bg-footer)' }}
                role="contentinfo"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center mb-4">
                                <Link
                                    to={user ? '/dashboard' : '/'}
                                    className="focus:outline-none focus:ring-0"
                                    aria-label="MyEnAb — Back to top"
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                >
                                    <MyEnAbLogo className="w-48 h-14" />
                                </Link>
                            </div>
                            <p className="text-sm leading-relaxed max-w-md" style={{ color: 'var(--text-muted)' }}>
                                A global platform connecting people with disabilities, service providers, and NGOs.
                                Building community, breaking barriers, celebrating inclusion.
                            </p>
                            <div className="flex gap-2 mt-4" aria-hidden="true">
                                <span className="w-3 h-3 rounded-full bg-[#E40303]"></span>
                                <span className="w-3 h-3 rounded-full bg-[#FF8C00]"></span>
                                <span className="w-3 h-3 rounded-full bg-[#FFD700]"></span>
                                <span className="w-3 h-3 rounded-full bg-[#008026]"></span>
                                <span className="w-3 h-3 rounded-full bg-[#24408E]"></span>
                                <span className="w-3 h-3 rounded-full bg-[#732982]"></span>
                            </div>
                        </div>
                        <div>
                            <h2 className="font-lexend font-semibold mb-4 text-sm" style={{ color: 'var(--text-primary)' }}>Platform</h2>
                            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                                <li><Link to="/forums" className="hover:underline transition-colors">Forums</Link></li>
                                <li><Link to="/directory" className="hover:underline transition-colors">Directory</Link></li>
                                <li><Link to="/events" className="hover:underline transition-colors">Events</Link></li>
                                <li><Link to="/resources" className="hover:underline transition-colors">Resources</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h2 className="font-lexend font-semibold mb-4 text-sm" style={{ color: 'var(--text-primary)' }}>Support</h2>
                            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                                <li><Link to="/accessibility" className="hover:underline transition-colors">Accessibility</Link></li>
                                <li><Link to="/privacy" className="hover:underline transition-colors">Privacy Policy</Link></li>
                                <li><Link to="/terms" className="hover:underline transition-colors">Terms of Service</Link></li>
                                <li><Link to="/contact" className="hover:underline transition-colors">Contact Us</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div
                        className="border-t mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm"
                        style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}
                    >
                        <p>&copy; {new Date().getFullYear()} MyEnAb. All rights reserved.</p>
                        {typeof visitCount === 'number' && (
                            <div
                                className="flex items-center gap-2 px-3 py-1 rounded-full border"
                                style={{ backgroundColor: 'var(--hover-overlay)', borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                <span>{visitCount.toLocaleString()} Site Visits</span>
                            </div>
                        )}
                    </div>
                </div>
            </footer>

            {/* Global Post Modal — lazy loaded to keep ProseMirror out of the initial bundle */}
            <Suspense fallback={null}>
                <PostModal />
            </Suspense>
        </div>
    );
};

export default Layout;
